import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AUTH_STORAGE_EVENT, getStoredToken } from '../utils/auth';

type PartnerMode = 'canvasser' | 'partner_admin';

interface PartnerModeContextType {
  mode: PartnerMode;
  setMode: (mode: PartnerMode) => void;
  toggleMode: () => void;
}

const PartnerModeContext = createContext<PartnerModeContextType | undefined>(undefined);

export const usePartnerMode = () => {
  const context = useContext(PartnerModeContext);
  if (context === undefined) {
    throw new Error('usePartnerMode must be used within a PartnerModeProvider');
  }
  return context;
};

interface PartnerModeProviderProps {
  children: ReactNode;
}

export const PartnerModeProvider: React.FC<PartnerModeProviderProps> = ({ children }) => {
  const [mode, setModeState] = useState<PartnerMode>(() => {
    const savedMode = localStorage.getItem('partnerMode');
    return (savedMode as PartnerMode) || 'partner_admin';
  });

  const setMode = (newMode: PartnerMode) => {
    setModeState(newMode);
    localStorage.setItem('partnerMode', newMode);
  };

  const toggleMode = () => {
    const newMode = mode === 'canvasser' ? 'partner_admin' : 'canvasser';
    setMode(newMode);
  };

  useEffect(() => {
    localStorage.setItem('partnerMode', mode);
  }, [mode]);

  useEffect(() => {
    const resetModeOnLogout = () => {
      if (!getStoredToken()) {
        setModeState('partner_admin');
      }
    };

    window.addEventListener(AUTH_STORAGE_EVENT, resetModeOnLogout);
    return () => window.removeEventListener(AUTH_STORAGE_EVENT, resetModeOnLogout);
  }, []);

  return (
    <PartnerModeContext.Provider value={{ mode, setMode, toggleMode }}>
      {children}
    </PartnerModeContext.Provider>
  );
};
